<template>
  <InputLabel :label="label" :tooltip="tooltip" :is-error="!!error" :class="props.class">
    <div class="relative">
      <div
        ref="reference"
        class="relative min-w-0"
        :class="{ 'min-w-0': variant === 'inline', 'pr-6': clearable && hasCommittedValue }"
      >
        <Input
          ref="input"
          v-model="searchString"
          list="location"
          :placeholder="effectivePlaceholder"
          :disabled="disabled"
          :variant="variant"
          :size="size"
          autocomplete="nope"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.stop="handleKeyDown"
        />
        <button
          v-if="clearable && hasCommittedValue"
          type="button"
          class="absolute right-1 top-1/2 z-10 -translate-y-1/2 p-0.5 text-obsidian-400 transition-colors hover:text-obsidian-600"
          :title="t('clearSelection')"
          @mousedown.prevent="handleClear"
        >
          <XIcon class="size-3.5" />
        </button>
        <Spinner
          v-if="isFetchingResults && searchString"
          class="absolute top-1/2 h-5 w-5 -translate-y-1/2"
          :class="clearable && hasCommittedValue ? 'right-6' : 'right-1.5'"
        />
      </div>
      <InputHint :hint="error" is-error />
      <Teleport to="body">
        <div
          v-if="showDropdown"
          ref="floating"
          :style="floatingStyles"
          :class="float({ class: 'z-[1000] max-h-[60vh] overflow-auto p-2' })"
          data-testid="search-dropdown"
        >
          <div
            v-for="(option, i) in searchOptions"
            :key="i"
            class="cursor-pointer pl-2 pr-2 hover:bg-gray-100"
            :class="{ 'bg-gray-100': option.isSelected }"
            @mousedown="selectOption(i)"
          >
            <div class="border-primary/40 pb-1 pt-1" :class="{ 'border-b': !option.isLast }">
              <div
                v-for="(line, o) in option.content"
                :key="o"
                class="text-sm"
                :class="{ 'text-xs': o > 0 }"
              >
                {{ line }}
              </div>
            </div>
          </div>
          <div v-if="searchOptions.length === 0" class="text-center text-obsidian-400">
            {{ t('noResults') }}
          </div>
        </div>
      </Teleport>
    </div>
  </InputLabel>
</template>

<script lang="ts" setup>
import Input from '@/components/Input.vue';
import { computed, onMounted, ref, toRef, watch, nextTick } from 'vue';
import InputHint from './InputHint.vue';
import InputLabel from './InputLabel.vue';
import { debounce } from 'lodash';
import { float } from '@carvia/theme/lib/cva/float';
import { useI18n } from 'vue-i18n';
import {
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
  useFloating,
} from '@floating-ui/vue';
import { XIcon } from 'lucide-vue-next';

export interface SearchResultOption {
  item: any;
  content: (string | null | undefined)[];
}

const props = withDefaults(
  defineProps<{
    searchResultOptions: SearchResultOption[];
    isFetchingResults: boolean;
    label?: string;
    tooltip?: string;
    placeholder?: string;
    error?: string;
    initialValue?: string;
    variant?: 'default' | 'inline';
    size?: 'default' | 'sm' | 'lg';
    disabled?: boolean;
    class?: string;
    clearable?: boolean;
  }>(),
  {
    variant: 'default',
    size: 'sm',
    disabled: false,
    clearable: false,
  },
);

const emit = defineEmits<{
  (e: 'fetch', value: string): void;
  (e: 'update-selected-item', value: any): void;
}>();

const { t } = useI18n();

const input = ref();
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const isFetchingResults = toRef(props, 'isFetchingResults');
const selectedResult = ref(0);

let justSelectedItem = false;

const searchOptions = computed(() =>
  props.searchResultOptions.map((option, i, results) => ({
    ...option,
    isSelected: i === selectedResult.value,
    isLast: i === results.length - 1,
  })),
);

const searchString = ref('');
const showDropdown = ref(false);
const isFocused = ref(false);
const isEditing = ref(false);
const hasCommittedValue = computed(() => !!props.initialValue);
const effectivePlaceholder = computed(() => {
  if (props.clearable && isEditing.value && props.initialValue) {
    return props.initialValue;
  }
  return props.placeholder;
});
const fetchDebounced = debounce(() => {
  showDropdown.value = !!searchString.value;
  emit('fetch', searchString.value);
}, 500);
watch(searchString, () => {
  if (props.disabled) return;
  if (!justSelectedItem && searchString.value !== props.initialValue) {
    if (!props.clearable && !searchString.value) {
      emit('update-selected-item', undefined);
    }
    selectedResult.value = 0;
    fetchDebounced();
  }
  justSelectedItem = false;
});

const onFocus = () => {
  isFocused.value = true;
  if (props.clearable && props.initialValue) {
    isEditing.value = true;
    searchString.value = '';
    selectedResult.value = 0;
  }
};

const onBlur = () => {
  showDropdown.value = false;
  isFocused.value = false;
  if (props.clearable && isEditing.value) {
    isEditing.value = false;
    if (props.initialValue) {
      searchString.value = props.initialValue;
    }
  }
};

const handleClear = () => {
  isEditing.value = false;
  searchString.value = '';
  showDropdown.value = false;
  justSelectedItem = true;
  emit('update-selected-item', undefined);
  input.value?.blur?.();
};

const handleKeyDown = (e: KeyboardEvent) => {
  if (!showDropdown.value) return;

  switch (e.key) {
    case 'ArrowDown':
      selectedResult.value = Math.min(
        selectedResult.value + 1,
        (searchOptions.value?.length ?? 1) - 1,
      );
      e.preventDefault();
      break;
    case 'ArrowUp':
      selectedResult.value = Math.max(selectedResult.value - 1, 0);
      e.preventDefault();
      break;
    case 'Enter':
      if (!searchOptions.value?.[selectedResult.value]) return;
      selectOption(selectedResult.value);
      e.preventDefault();
      break;
  }
};

const selectOption = (index: number) => {
  if (!(index in searchOptions.value)) return;
  selectedResult.value = index;
  searchString.value = searchOptions.value[index].content?.[0] ?? '';
  showDropdown.value = false;
  isEditing.value = false;
  justSelectedItem = true;
  input.value?.blur?.();
  emit('update-selected-item', searchOptions.value[index].item);
};

watch(searchOptions, () => {
  selectedResult.value = Math.min(
    Math.max(selectedResult.value, 0),
    (searchOptions.value?.length ?? 1) - 1,
  );
});

onMounted(() => {
  if (props.initialValue) {
    searchString.value = props.initialValue;
  }
});

const { floatingStyles, update } = useFloating(reference, floating, {
  placement: 'bottom-start',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(4),
    flip(),
    shift({ padding: 8 }),
    floatingSize({
      apply({ rects, elements, availableHeight }) {
        if (!elements.floating) return;
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxHeight: `${Math.max(200, Math.min(availableHeight, 480))}px`,
        });
      },
    }),
  ],
});

watch(showDropdown, async (open) => {
  if (open) await nextTick().then(() => update());
});

watch(
  () => props.initialValue,
  (val) => {
    if (isFocused.value) return;
    searchString.value = val ?? '';
  },
);
</script>

<i18n lang="json">
{
  "en": {
    "noResults": "No results",
    "clearSelection": "Clear selection"
  },
  "de": {
    "noResults": "Keine Ergebnisse",
    "clearSelection": "Auswahl löschen"
  },
  "es": {
    "noResults": "Sin resultados",
    "clearSelection": "Borrar selección"
  }
}
</i18n>
